import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { debounce, isEmpty, isEqual } from "lodash";
import { Button, Checkbox, Dialog, H2 } from "@blueprintjs/core";

import { loginUser, selectLoginUserError, selectLoginUser, selectLoginUserRequest } from "controllers/user/action";
import { selectCurrentDialogue, setCurrentDialogue } from "controllers/common/action";
import { validate, SingleInputType } from "utils/form";

import ForgotForm from "components/ForgotForm";
import SingleFormInput from "../SingleFormInput";

import "./Login.scss";
import { Analytics } from "utils/analytics";

interface LoginProps {
  isOpen?: boolean;
  onClose?: () => void;
  goHome?: boolean;
}

export default function Login(props: LoginProps) {
  const { isOpen, onClose, goHome } = props;
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const success = useSelector(selectLoginUser);
  const error = useSelector(selectLoginUserError);
  const request = useSelector(selectLoginUserRequest);

  const [showPW, setShowPW] = useState(false);
  const [formInfo, setFormInfo] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({ email: "", password: "" });
  const currentDialogue = useSelector(selectCurrentDialogue);

  useEffect(() => {
    return () => {
      setFormInfo({ email: "", password: "" });
      setErrors({ email: "", password: "" });
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(success)) {
      setFormInfo({ email: "", password: "" });
      setErrors({ email: "", password: "" });
      navigate("/dashboard");
    }
    if (!isEmpty(error)) {
      dispatch(setCurrentDialogue("Login"));
    }
  }, [success, error, navigate, dispatch]);

  const shouldOpen = isOpen || currentDialogue === "Login";
  const shouldClose = () => {
    setErrors({ email: "", password: "" });
    dispatch(setCurrentDialogue(""));
    onClose?.();
  };

  useEffect(() => {
    if (shouldOpen) {
      Analytics.getInstance().event("Login", "Opened");
    }
    return () => { };
  }, [shouldOpen]);

  const handleChange = (event: any) => {
    event.preventDefault();
    const { name, value } = event.target;
    setFormInfo((state) => ({ ...state, [name]: value }));
    debounceField({ name, value });
  };
  const handleSignUp = () => {
    dispatch(setCurrentDialogue("SignUp"));
    onClose?.();
  };
  const handleShowPW = () => setShowPW((state) => !state);

  const handleForgot = () => {
    dispatch(setCurrentDialogue("Forgot"));
    onClose?.();
  };

  const debounceField = debounce(({ name, value }) => {
    let validName = "text";
    const { isValid, getMessage } = validate(validName);
    if (!isValid(value, [])) {
      setErrors((state) => ({ ...state, [name]: getMessage(value, []) }));
    } else {
      setErrors((state) => ({ ...state, [name]: "" }));
    }
  }, Number(process.env.REACT_APP_DEBOUNCE));

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const payload = formInfo;
    const emptyKeys = Object.keys(payload).filter((key) => !payload[key as keyof typeof payload]);
    emptyKeys.forEach((key) => {
      setErrors((state) => ({ ...state, [key as keyof typeof errors]: `Please provide required input` }));
    });
    if (Object.keys(errors).every((key) => !errors[key as keyof typeof errors])) {
      dispatch(loginUser(payload));
    }
  };

  const handleRedirect = () => navigate("/");

  const LoginForm: SingleInputType[] = [
    {
      name: "email",
      type: "text",
      label: "Email/Username",
      labelInfo: "*",
      value: formInfo.email,
      placeholder: "email or username",
    },
    {
      name: "password",
      type: showPW ? "text" : "password",
      label: "Password",
      labelInfo: "*",
      value: formInfo.password,
    },
  ];

  return (
    <Fragment>
      <Dialog
        className="login-dialog"
        isOpen={shouldOpen}
      >
        <div className="form__header">
          <H2>Log In</H2>
          <Button minimal intent="primary" icon="cross" onClick={onClose ? shouldClose : handleRedirect} />
        </div>

        <form onSubmit={handleSubmit}>
          {error && (isEmpty(formInfo.password) || isEqual(formInfo, request)) ? (
            <p className="red helperText">{error.error}</p>
          ) : (
            ""
          )}
          {LoginForm.map((input) => {
            input.handleChange = handleChange;
            input.error = errors[input.name as keyof typeof errors];
            return <SingleFormInput {...input} key={input.name} />;
          })}

          <div className="form--oneline">
            <Checkbox checked={showPW} onChange={handleShowPW}>
              {"Show Password"}
            </Checkbox>
            <Button
              className="login__button-forgot"
              minimal
              intent="primary"
              key="forgot"
              text="Forgot password?"
              onClick={handleForgot}
            />
          </div>

          <div className="form__footer desktop-footer">
            {!goHome && (
              <Button
                className="button-left"
                minimal
                intent="primary"
                key="signup"
                icon="new-person"
                text="Create Account"
                onClick={handleSignUp}
              />
            )}
            {goHome && (
              <Button
                className="button-left"
                icon="arrow-left"
                minimal
                intent="primary"
                key="goHome"
                text="Go Back Home"
                onClick={handleRedirect}
              />
            )}
            <div>
              <Button
                className="button-right"
                minimal
                intent="primary"
                key="cancel"
                text="Cancel"
                onClick={onClose ? shouldClose : handleRedirect}
              />
              <Button className="button-submit" intent="primary" icon="log-in" type="submit" text="Log In" />
            </div>
          </div>
          <div className="form__footer mobile-footer">
            <Button className="button-submit" intent="primary" icon="log-in" type="submit" text="Log In" />
            <div className="secondary-buttons">
              {!goHome ? (
                <Button
                  className="button-left"
                  minimal
                  intent="primary"
                  key="signup"
                  icon="new-person"
                  text="Create Account"
                  onClick={handleSignUp}
                />
              ) : (
                <Button
                  className="button-left"
                  icon="arrow-left"
                  minimal
                  intent="primary"
                  key="goHome"
                  text="Go Back Home"
                  onClick={handleRedirect}
                />
              )}
            </div>
          </div>
        </form>
      </Dialog>
      <ForgotForm />
    </Fragment>
  );
}
